/**
 * Investor area: the door and the panel.
 *
 * See the Spanish file for why this is a fraction of what it used to be. The
 * shape is enforced by the `Mensajes` type: if Spanish grows a key and this one
 * does not, the build fails.
 */

export const area = {
  comun: {
    cargando: 'Loading…',
    reintentar: 'Try again',
  },

  mapa: {
    descripcion: 'Tile map of Mexico: each state is a square, lit when it has records.',
    estadoConRegistros: (nombre: string, n: number) =>
      `${nombre}: ${n} ${n === 1 ? 'record' : 'records'}`,
    estadoSinRegistros: (clave: string) => `${clave}: no records yet`,
    leyenda: {
      sinSenal: 'No signal',
      uno: '1 record',
      dos: '2 records',
      tresOmas: '3 or more',
    },
  },

  /**
   * Rewritten on 2026-09-20, not recovered: the previous version described a
   * self-service sign-up with an allow-list and an approval queue. Access is by
   * invitation now, and nothing here promises an email.
   */
  acceso: {
    titulo: 'Investor access',
    subtitulo:
      'We publish our traction — how many people show interest, demo conversion and how it moves month to month — for anyone considering investing.',

    porInvitacion:
      'Access is by invitation. If the team gave you an account, sign in with it; if not, write to us and we will set one up.',

    deMentiraAntes: 'This sign-in is ',
    deMentiraFuerte: 'a simulation',
    deMentiraDespues:
      ': it validates nothing against any server, and the accounts are written into the site itself. Do not type a password you use anywhere else.',

    correo: 'Email',
    correoRequerido: 'Enter the email we issued your account with.',
    contrasena: 'Password',
    mostrarContrasena: 'Show password',
    ocultarContrasena: 'Hide password',
    olvidaste: 'Forgot your password?',
    contrasenaRequerida: 'Enter your password.',
    entrar: 'Sign in',
    entrando: 'Signing in…',
    escribirnos: 'Write to us',
    cerrarSesion: 'Sign out',

    avisoAntes: 'You will see ',
    avisoFuerte: 'aggregate figures only',
    avisoDespues:
      ': how many people showed interest, how many requested a demo and how it moves month to month. The personal data of the people who register never leaves the system.',

    yaEntraste: (nombre: string) => `Signed in as ${nombre}.`,
    verTraccion: 'See the traction',
  },

  inversionistas: {
    titulo: 'Traction',
    subtitulo: 'aggregate figures only',
    cargando: 'Loading metrics…',
    sinDatos: 'There is not enough data yet.',
    falloCargar: 'We could not load the metrics.',
    actualizado: (cuando: string) => `Interest recorded on the platform. Updated ${cuando}.`,
    avisoAgregadoAntes: 'This panel shows ',
    avisoAgregadoFuerte: 'aggregate figures only',
    avisoAgregadoDespues:
      '. The personal data of the people who register never leaves the system: the aggregation happens on the server, so it does not even travel to this browser.',

    cobertura: {
      titulo: 'Where there is already signal',
      subtitulo: 'Contacts captured in the field with venues, hotels and convention bureaus.',
      falloCargar: 'Coverage could not be loaded.',
      kpis: {
        registros: 'Contacts',
        organizaciones: 'Organizations',
        estados: 'States with signal',
        internacionales: 'Outside Mexico',
        pendientes: 'Cards to transcribe',
      },
      porTipo: 'By organization type',
      nota: 'Counts by state are published. Individual organizations are not listed.',
      sinCobertura: 'Territorial coverage could not be read right now.',
      cargando: 'Loading coverage…',
      todaviaNo:
        'Field work is under way. Counts by state will appear here once the panel reads them from the server.',
    },

    tarjetas: {
      solicitudesDeContacto: 'Contact requests',
      cuentasCreadas: 'Accounts created',
      pidieronDemo: 'Requested a demo',
      enEspera: 'Waiting',
      demosAtendidas: 'Demos delivered',
      conversionADemo: 'Demo conversion',
      conversionPista: 'of those who create an account',
    },

    embudo: {
      titulo: 'Site funnel',
      sinDatos:
        'The contact form and account sign-ups have not recorded any activity yet. Figures will appear here as they arrive.',
    },

    distribuciones: {
      porIndustria: 'By industry',
      porTamano: 'By company size',
      porTipoDeSolicitud: 'By request type',
      porPerfil: 'By declared profile',
    },

    demo: {
      titulo: 'See the product at work',
      descripcion:
        'A demo of how the sensors measure a booth: visitor paths, people per zone and lead capture with a badge.',
      cta: 'Open the demo',
    },

    evolucionMensual: 'Monthly trend',
    series: {
      cuentasPorMes: 'Accounts created per month',
      solicitudesPorMes: 'Requests per month',
    },
  },

  portero: {
    titulo: 'Who has access',
    subtitulo: 'The investor area is invitation-only: there is no open sign-up.',
    aviso:
      'The server only serves this screen to accounts with the administrator role. Hiding the link from everyone else is a courtesy, not the protection.',
    invitar: {
      titulo: 'Invite someone',
      descripcion:
        'This creates the account and draws a random password. Whoever signs in with it will have to change it.',
      correo: 'Email',
      nombre: 'Name (optional)',
      organizacion: 'Organisation (optional)',
      cta: 'Create the account',
      enviando: 'Creating…',
      creada: (correo: string) => `Account created for ${correo}.`,
      unaSolaVez:
        'Copy the password now: it is stored hashed and cannot be looked up again. Send it through a channel other than the invitation email.',
    },
    lista: {
      titulo: 'Registered accounts',
      vacia: 'No accounts yet.',
      correo: 'Email',
      nombre: 'Name',
      rol: 'Role',
      alta: 'Created',
      ultimoIngreso: 'Last sign-in',
      estado: 'Status',
      sinNombre: 'No name',
      nunca: 'Never signed in',
      activa: 'Active',
      sinEstrenar: 'Not used yet',
      revocada: 'Revoked',
    },
    sucesos: {
      titulo: 'What has happened',
      explicacion:
        'The password recovery screen cannot say whether an email went out —answering differently would reveal which addresses exist— so failures show up here. Today sending is rejected except to verified addresses: SES is in sandbox mode.',
      cuando: 'When',
      que: 'What',
      detalle: 'Detail',
    },
    volver: 'Back to the dashboard',
  },

  recuperar: {
    titulo: 'Change your password',
    subtitulo: 'We send a code to the email you sign in with, and you change it right here.',
    volver: 'Back to sign in',
    listo: 'Password changed. You can sign in with the new one.',
    entrar: 'Go to sign in',
    paso1: {
      explicacion:
        'Enter your account email. If it is registered, you will get a single-use code that expires in 15 minutes.',
      cta: 'Send me the code',
      enviando: 'Sending…',
    },
    paso2: {
      avisoAntes: 'If ',
      avisoDespues:
        ' is registered, the code is on its way. It may take a couple of minutes; if it does not arrive, write to us.',
      codigo: 'Code',
      ejemplo: 'ABCDE-FGHJK',
      nueva: 'New password',
      minimo: 'At least 12 characters. Long beats complicated.',
      cta: 'Change the password',
      enviando: 'Changing…',
      otroCodigo: 'Request another code',
    },
  },

  errores: {
    credenciales: 'That email and password do not match.',
    red: 'We could not reach the server. Check your connection and try again.',
    sinConfigurar: 'Access is not configured in this environment yet.',
    servidor: 'The server returned an error. Try again in a moment.',
    sesionExpirada: 'Your session expired. Sign in again.',
    sinPermiso: 'Your account does not have access to this section. Ask to be granted it.',
    codigoInvalido: 'That code is not valid. Check it or request a new one.',
    codigoCaducado: 'The code expired. Request a new one.',
    contrasenaCorta: 'The new password is too short.',
    generico: 'We could not complete that.',
  },
};
